/**
 * Magic UI — Border Beam
 * https://magicui.design/docs/components/border-beam
 *
 * SVG rounded-rect stroke with CSS @keyframes on stroke-dashoffset — seamless infinite loop
 * along the border (one lap = 100 path units). Place last inside `relative overflow-hidden rounded-*`.
 */
import { useId, useMemo } from 'react';
import type { Transition } from 'framer-motion';
import { cn } from '../../lib/utils';

export interface BorderBeamProps {
  className?: string;
  /** Length of the lit segment along the path (maps to Magic UI “size”). */
  size?: number;
  duration?: number;
  delay?: number;
  colorFrom?: string;
  colorTo?: string;
  transition?: Transition;
  style?: React.CSSProperties;
  reverse?: boolean;
  /** Starting position along the path (0–100, one full lap). */
  initialOffset?: number;
  borderWidth?: number;
}

export function BorderBeam({
  className,
  size = 50,
  delay = 0,
  duration = 6,
  colorFrom = '#ffaa40',
  colorTo = '#9c40ff',
  transition,
  style,
  reverse = false,
  initialOffset = 0,
  borderWidth = 1.5,
}: BorderBeamProps) {
  const sid = useId().replace(/:/g, '');
  const gradId = `bb-grad-${sid}`;
  const animName = `bb-loop-${sid}`;

  const beamPct = Math.min(0.28, Math.max(0.05, size / 360));
  const dashSolid = beamPct * 100;
  const dashGap = 100 - dashSolid;

  const phase = ((initialOffset % 100) + 100) % 100;
  const fromOff = phase;
  const toOff = reverse ? phase - 100 : phase + 100;

  const durSec =
    typeof transition?.duration === 'number' && !Array.isArray(transition.duration)
      ? transition.duration
      : duration;

  const keyframesCss = useMemo(
    () => `
@keyframes ${animName} {
  from { stroke-dashoffset: ${fromOff}; }
  to { stroke-dashoffset: ${toOff}; }
}
@media (prefers-reduced-motion: reduce) {
  .bb-rect-${sid} { animation: none !important; stroke-dashoffset: ${fromOff} !important; }
}
`,
    [animName, fromOff, toOff, sid],
  );

  return (
    <div
      className={cn('pointer-events-none absolute inset-0 z-0 overflow-hidden rounded-[inherit]', className)}
      style={style}
    >
      <style>{keyframesCss}</style>
      <svg
        className="h-full w-full"
        viewBox="0 0 100 100"
        fill="none"
        preserveAspectRatio="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
      >
        <defs>
          <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={colorFrom} />
            <stop offset="100%" stopColor={colorTo} />
          </linearGradient>
        </defs>
        <rect
          className={`bb-rect-${sid}`}
          x={0}
          y={0}
          width={100}
          height={100}
          rx={4}
          ry={4}
          stroke={`url(#${gradId})`}
          strokeWidth={borderWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="nonScalingStroke"
          fill="none"
          pathLength={100}
          strokeDasharray={`${dashSolid} ${dashGap}`}
          style={{
            animation: `${animName} ${durSec}s linear infinite`,
            animationDelay: `${delay}s`,
            willChange: 'stroke-dashoffset',
          }}
        />
      </svg>
    </div>
  );
}
